import axios from 'src/store/vue-axios/axios.js'
import GlobalPrototypes from '@/config/services/globalPrototypes.ts'

export default class Timetable {
  static CLASSBOOK_TIMEOUT = import.meta.env.VITE_CLASSBOOK_EXTENDED_REQUEST_TIMEOUT

  // ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // API ENDPOINTS ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

  // import.meta.env.VITE_TIMETABLE_API_URL * special cases
  static timetableAuthLoginEnvUrl = () => `${import.meta.env.VITE_TIMETABLE_API_URL}/auth/login`
  static timetableUploadEnvUrl = (selectedSystem, sync) => `${import.meta.env.VITE_TIMETABLE_API_URL}/upload/${selectedSystem}/web-1.0.0?${sync}`
  static timetableUploadResetHashEnvUrl = () => `${import.meta.env.VITE_TIMETABLE_API_URL}/upload/reset-hashes`
  static timetableReprocessEnvUrl = () => `${import.meta.env.VITE_TIMETABLE_API_URL}/reprocess`
  static timetableExternalSchoolSyncUrl = () => `${import.meta.env.VITE_TIMETABLE_API_URL}/timetables/external-school-sync`

  // TIMETABLES -> /timetables/*
  static timetableTimesUrl = () => '/timetables/times'
  static timetableSubjectsUrl = () => '/timetables/subjects'
  static timetableBookablesUrl = () => '/timetables/bookables'
  static timetableHolidaysUrl = () => '/timetables/holidays'
  static timetableGradesUrl = () => '/timetables/grades'
  static timetableTeachersUrl = () => '/timetables/teachers'
  static timetableSingleSubjectUrl = (subjectId) => `/timetables/subjects/${subjectId}`
  static timetableEntitiesUrl = (entityName, isPaginated, page, searchVal, types) => `/timetables/${entityName}/?is_paginated=${Number(isPaginated)}&page=${page}&search=${searchVal}&types=${types}`
  static timetableSingleEntityUrl = (entityName, entityId) => `/timetables/${entityName}/${entityId}`
  static timetableSingleEntityTTUrl = (entityName, entityId) => `/timetables/${entityName}/${entityId}/timetable`
  static timetableSingleEntityTTRangeUrl = (entityType, entityId, entityBeginsAt, entityEndsAt) => `timetables/${entityType}/${entityId}/timetable?begins_at=${entityBeginsAt}&ends_at=${entityEndsAt}`

  // EDITOR -> /timetables/editor*
  static timetableEditorPushEntityUrl = (entityName) => `/timetables/editor/push/${entityName}`
  static timetableEditorPopEntityUrl = (entityName) => `/timetables/editor/pop/${entityName}`
  static timetableEditorPublishUrl = () => '/timetables/editor/publish'
  static timetableEditorVersionUrl = () => '/timetables/editor/versions'
  static timetableEditorCurrentUrl = () => '/timetables/editor/current'
  static timetableEditorLessonsUrl = () => '/timetables/editor/lessons'
  static timetableEditorCoursesUrl = () => '/timetables/editor/courses'

  // COURSES -> /timetables/courses/${courseId}*
  static timetableCourseUrl = (courseId) => `/timetables/courses/${courseId}`
  static timetableCoursesTimetableUrl = (courseId, begins_at, ends_at) => `/timetables/courses/${courseId}/timetable?begins_at=${begins_at}&ends_at=${ends_at}`

  // LESSONS-CLASSBOOK-ENTRY -> /timetables/lessons/${lessonId}/classbook-entry
  static timetableLessonsCBEntryByDateUrl = (lessonId) => `/timetables/lessons/${lessonId}/classbook-entry`

  // ENROLLMENTS -> /timetables/courses/${courseId}/enrollments*
  static timetableGradeEnrollmentsPaginatedSearchUrl = (courseId, state, page, searchVal) => `/timetables/courses/${courseId}/enrollments?state=${state}&page=${page}&search=${searchVal}`
  static timetableCourseEnrollmentsPaginatedSearchUrl = (courseId, state, page, searchVal) => `/timetables/courses/${courseId}/enrollments/enrolled?state=${state}&page=${page}&search=${searchVal}`
  static timetableEnrollmentsUpdateAllUrl = (courseId) => `/timetables/courses/${courseId}/enrollments/update-all`
  static timetableEnrollmentsUpdateUrl = (courseId) => `/timetables/courses/${courseId}/enrollments/update`
  static timetableCourseEnrollmentsUpdateUrl = (courseId) => `/timetables/courses/${courseId}/enrollments/updateCourseEnrollment`
  static timetableCourseEnrollmentsRevokeUrl = (courseId, courseEnrollment) => `/timetables/courses/${courseId}/enrollments/${courseEnrollment}/revoke`

  // TIMETABLES -> /timetables/${timetableId}*
  static timetableUrl = (timetableId) => `/timetables/${timetableId}`
  static timetableAppInfoUrl = (timetableId) => `/timetables/${timetableId}/download`
  static timetableUpgradeGradesUrl = (timetableId) => `/timetables/${timetableId}/upgrade-grades`
  static timetableSwitchSchoolyearUrl = (timetableId) => `/timetables/${timetableId}/switch-schoolyear`
  static timetableDeleteYearUrl = (timetableId, schoolYearId) => `/timetables/${timetableId}/years/${schoolYearId}`

  // TIMETABLE -> /timetable/${timetableId}*
  static timetableSingleSchoolYearsUrl = (timetableId) => `/timetable/${timetableId}`

  // YEARS -> /timetables/${timetableId}/years/${activeSchoolYearId}*
  static timetableSchoolYearsUrl = (timetableId) => `/timetables/${timetableId}/years`
  static timetableSchoolYearsUploadGroupsUrl = (timetableId, schoolYearId) => `/timetables/${timetableId}/years/${schoolYearId}/uploads/groups?`

  // GRADES -> /timetables/${timetableId}/years/${activeSchoolYearId}/grades*
  static timetableSchoolYearGradesUrl = (timetableId, schoolYearId) => `/timetables/${timetableId}/years/${schoolYearId}/grades`

  // PERIODS -> /timetables/${timetableId}/years/${activeSchoolYearId}/periods*
  static timetableSchoolYearPeriodsUrl = (timetableId, schoolYearId) => `/timetables/${timetableId}/years/${schoolYearId}/periods`
  static timetableSchoolYearPeriodsDiffUrl = (timetableId, schoolYearId) => `/timetables/${timetableId}/years/${schoolYearId}/periods/get-diff`
  static timetableSchoolYearPeriodsEntityDiffUrl = (timetableId, schoolYearId, entityName) => `/timetables/${timetableId}/years/${schoolYearId}/periods/entity-diff?entity=${entityName}`

  // USERS -> /timetables/users/${userId}*
  static timetableAvailableUrl = (userId) => `timetables/users/${userId}/available`
  static timetableCurseSelectionConflictsUrl = (userId) => `timetables/users/${userId}/courses/conflicts`

  // ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // API IMPLEMENTATIONS ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

  // import.meta.env.VITE_TIMETABLE_API_URL * special cases
  static async postTimetableAuthLoginEnv (payload, config) {
    return axios.post(this.timetableAuthLoginEnvUrl(), payload, config)
  }

  static async postTimetableUploadEnv (selectedSystem, sync, payload, config) {
    return axios.post(this.timetableUploadEnvUrl(selectedSystem, sync), payload, config)
  }

  static async putTimetableUploadResetHashEnv (payload, config) {
    return axios.put(this.timetableUploadResetHashEnvUrl(), payload, config)
  }

  static async putTimetableReprocessEnv (payload, config) {
    return axios.put(this.timetableReprocessEnvUrl(), payload, config)
  }

  static async postTimetableExternalSchoolSync (payload, config) {
    return axios.post(this.timetableExternalSchoolSyncUrl(), payload, config)
  }

  // TIMETABLES -> /timetables/*
  static async getTimetableSubject (subjectId) {
    return axios.get(this.timetableSingleSubjectUrl(subjectId))
  }

  static async getTimetableEntities (entityName, isPaginated, page, searchVal, types) {
    return GlobalPrototypes.cacheHttp(this.timetableEntitiesUrl(entityName, isPaginated, page, searchVal, types))
  }

  static async getTimetableSingleEntity (entityName, entityId) {
    return axios.get(this.timetableSingleEntityUrl(entityName, entityId))
  }

  static async getTimetableSingleEntityTT (entityName, entityId) {
    return GlobalPrototypes.cacheHttp(this.timetableSingleEntityTTUrl(entityName, entityId))
  }

  // TIMETABLES -> /timetables/${timetableId}*
  static async getTimetable (timetableId) {
    return axios.get(this.timetableUrl(timetableId))
  }

  static async getTimetableSchoolYears (timetableId) {
    return axios.get(this.timetableSchoolYearsUrl(timetableId))
  }

  static async getTimetableAppInfo (timetableId) {
    return axios.get(this.timetableAppInfoUrl(timetableId))
  }

  static async postTimetableUpgradeGrades (timetableId, payload, config) {
    return axios.post(this.timetableUpgradeGradesUrl(timetableId), payload, config)
  }

  static async postTimetableSwitchSchoolyear (timetableId, config) {
    return axios.post(this.timetableSwitchSchoolyearUrl(timetableId), config)
  }

  static async deleteTimetableYear (timetableId, schoolYearId) {
    return axios.delete(this.timetableDeleteYearUrl(timetableId, schoolYearId))
  }

  // TIMETABLE -> /timetable/${timetableId}*
  static async getTimetableSingleSchoolYears (timetableId) {
    return axios.get(this.timetableSingleSchoolYearsUrl(timetableId))
  }

  // GRADES -> /timetables/${timetableId}/years/${activeSchoolYearId}/grades*
  static async getTimetableGrades (timetableId, schoolYearId) {
    return axios.get(this.timetableSchoolYearGradesUrl(timetableId, schoolYearId))
  }

  // EDITOR -> /timetables/editor*
  static async postTimetableEditorPushEntity (entityName, payload, config) {
    return axios.post(this.timetableEditorPushEntityUrl(entityName), payload, config)
  }

  static async postTimetableEditorPopEntity (entityName, payload, config) {
    return axios.post(this.timetableEditorPopEntityUrl(entityName), payload, config)
  }

  static async postTimetableEditorPublish (payload, config) {
    return axios.post(this.timetableEditorPublishUrl(), payload, config)
  }

  static async getTimetableEditorVersion (config) {
    return axios.get(this.timetableEditorVersionUrl(), config)
  }

  static async postTimetableEditorVersion (payload) {
    return axios.post(this.timetableEditorVersionUrl(), payload)
  }

  static async getTimetableEditorCurrent (config) {
    return axios.get(this.timetableEditorCurrentUrl(), config)
  }

  static async getTimetableEditorLessons (config) {
    return axios.get(this.timetableEditorLessonsUrl(), config)
  }

  static async getTimetableEditorCourses (config) {
    return axios.get(this.timetableEditorCoursesUrl(), config)
  }

  // COURSES -> /timetables/courses/${courseId}*
  static async getTimetableCourses (courseId) {
    return axios.get(this.timetableCourseUrl(courseId))
  }

  static async getTimetableCoursesTimetable (courseId, begins_at, ends_at) {
    return axios.get(this.timetableCoursesTimetableUrl(courseId, begins_at, ends_at))
  }

  // LESSONS-CLASSBOOK-ENTRY -> /timetables/lessons/${lessonId}/classbook-entry
  static async getTimetableLessonsCBEntryByDate (lessonId) {
    return GlobalPrototypes.cacheHttp(this.timetableLessonsCBEntryByDateUrl(lessonId), { timeout: this.CLASSBOOK_TIMEOUT })
  }

  // ENROLLMENTS -> /timetables/courses/${courseId}/enrollments*
  static async getTimetableGradeEnrollmentsPaginatedSearch (courseId, state, page, searchVal) {
    return axios.get(this.timetableGradeEnrollmentsPaginatedSearchUrl(courseId, state, page, searchVal))
  }

  static async getTimetableCourseEnrollmentsPaginatedSearch (courseId, state, page, searchVal) {
    return axios.get(this.timetableCourseEnrollmentsPaginatedSearchUrl(courseId, state, page, searchVal), { timeout: this.CLASSBOOK_TIMEOUT })
  }

  static async putTimetableEnrollmentsUpdateAll (courseId, payload) {
    return axios.put(this.timetableEnrollmentsUpdateAllUrl(courseId), payload)
  }

  static async putTimetableEnrollmentsUpdate (courseId, payload) {
    return axios.put(this.timetableEnrollmentsUpdateUrl(courseId), payload)
  }

  static async deleteTimetableCourseEnrollmentsRevoke (courseId, courseEnrollment) {
    return axios.delete(this.timetableCourseEnrollmentsRevokeUrl(courseId, courseEnrollment))
  }

  static async putTimetableCourseEnrollmentsUpdate (courseId, payload) {
    return axios.put(this.timetableCourseEnrollmentsUpdateUrl(courseId), payload)
  }

  static async getTimetableAvailable (userId) {
    return axios.get(this.timetableAvailableUrl(userId))
  }

  static async getTimetableCurseSelectionConflicts (userId, payload) {
    return axios.get(this.timetableCurseSelectionConflictsUrl(userId), payload)
  }
}
